import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import {
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Checkbox,
  Chip
} from '@mui/material';
import {
  LocalShipping,
  Payment,
  CheckCircle,
  Add,
  CreditCard,
  AccountBalance,
  Money
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { cartService, orderService, shippingService } from '../services/api';
import { toast } from 'react-toastify';

const steps = ['Shipping', 'Payment', 'Review & Confirm'];

const Checkout = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { cart, clearCart } = useCart();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cartData, setCartData] = useState(null);
  
  // Shipping states
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [shippingOption, setShippingOption] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  
  // Payment states
  const [paymentMethod, setPaymentMethod] = useState('payu');
  const [codExtraCharge, setCodExtraCharge] = useState(0);
  const [billingAddressSame, setBillingAddressSame] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phoneNumber: ''
  });
  
  // Order states
  const [orderNotes, setOrderNotes] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // shippingOptions now stateful list from quote endpoint
  
  const paymentMethods = [
    { id: 'payu', name: 'Online Payment (PayU)', icon: CreditCard, description: 'Credit/Debit Card, Net Banking, UPI, Wallets' },
    { id: 'cod', name: 'Cash on Delivery', icon: Money, description: 'Pay when your order is delivered' }
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    
  fetchCartData();
    loadUserAddresses();
    // Fetch COD extra charge from shipping settings
    const fetchCodCharge = async () => {
      try {
        const res = await shippingService.getSettings();
        setCodExtraCharge(res.data.codExtraCharge || 0);
      } catch (e) {
        setCodExtraCharge(0);
      }
    };
    fetchCodCharge();
  }, [currentUser, navigate]);

  const fetchCartData = async () => {
    try {
      setLoading(true);
      const response = await cartService.getCart();
      const cart = response.data.cart;
      
      if (!cart || !cart.items || cart.items.length === 0) {
        toast.warning('Your cart is empty!');
        navigate('/cart');
        return;
      }
      
      setCartData(cart);
      // Fetch shipping quote once cart is known
      if (cart && cart.items && cart.items.length) {
        try {
          const subtotal = cart.total || cart.items.reduce((s,i)=>s + i.price * i.quantity, 0);
          const quoteRes = await shippingService.getQuote({ subtotal });
          const opts = quoteRes.data?.options || [];
          setShippingOptions(opts);
          setShippingOption(quoteRes.data?.selected || opts[0]?.id || null);
        } catch (e) {
          console.error('Shipping quote failed', e);
        }
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load cart data');
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const loadUserAddresses = async () => {
    try {
      if (!currentUser) return;
      
      console.log('Loading addresses for user:', currentUser.uid);
      
      // Load addresses from Firestore using direct imports
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data from Firestore:', userData);
        
        if (userData.addresses && userData.addresses.length > 0) {
          console.log('Found addresses:', userData.addresses);
          setAddresses(userData.addresses);
          setSelectedAddress(userData.addresses[0]);
          return;
        }
      }
      
      // If no addresses found, show empty state
      console.log('No addresses found in Firestore');
      setAddresses([]);
      setSelectedAddress(null);
      
    } catch (error) {
      console.error('Error loading addresses:', error);
      toast.error('Failed to load addresses');
      setAddresses([]);
      setSelectedAddress(null);
    }
  };

  const calculateTotal = () => {
    if (!cartData) return { subtotal: 0, shipping: 0, tax: 0, codCharge: 0, total: 0 };
    const subtotal = cartData.total || 0;
    const selectedShipping = shippingOptions.find(opt => opt.id === shippingOption);
    const shipping = selectedShipping ? selectedShipping.price : 0;
    // Tax is included in product price (18%)
    const taxIncluded = Math.round(subtotal * 0.18 / 1.18);
    const codCharge = paymentMethod === 'cod' ? codExtraCharge : 0;
    const total = subtotal + shipping + codCharge - (cartData.discount || 0);
    return {
      subtotal,
      shipping,
      tax: taxIncluded,
      codCharge,
      discount: cartData.discount || 0,
      total: Math.max(0, total)
    };
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!selectedAddress) {
        toast.error('Please select a delivery address');
        return;
      }
    } else if (activeStep === 1) {
      if (!paymentMethod) {
        toast.error('Please select a payment method');
        return;
      }
      if (!billingAddressSame && (!billingAddress.addressLine1 || !billingAddress.city || !billingAddress.state || !billingAddress.pincode || !billingAddress.phoneNumber)) {
        toast.error('Please fill in all billing address fields');
        return;
      }
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handlePlaceOrder = async () => {
    if (!acceptedTerms) {
      toast.error('Please accept terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const pricing = calculateTotal();
      const orderData = {
        items: cartData.items,
        shippingAddress: selectedAddress,
        billingAddress: billingAddressSame ? selectedAddress : billingAddress,
        paymentMethod,
        shippingOption,
        orderNotes,
        pricing,
        couponCode: cartData.couponCode,
        discount: cartData.discount,
        codCharge: pricing.codCharge
      };

      if(paymentMethod === 'payu') {
        // Create payment session instead of order
        const sessionRes = await orderService.createPaymentSession(orderData);
        if(sessionRes.data.success){
          initiatePayUPayment(sessionRes.data.paymentData);
        } else {
          toast.error('Failed to start payment session');
        }
      } else {
        // COD still creates order immediately
        const response = await orderService.createOrder(orderData);
        if (response.data.success) {
          const { orderId } = response.data;
          await clearCart();
          navigate(`/order-confirmation/${orderId}`, { state: { orderData: response.data } });
          toast.success('Order placed successfully!');
        }
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(error.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const initiatePayUPayment = (paymentData) => {
    // Create a form and submit it to PayU
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentData.paymentUrl;

    // Add all PayU parameters as hidden inputs
    Object.keys(paymentData.params).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = paymentData.params[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  if (loading && !cartData) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading checkout...
        </Typography>
      </Container>
    );
  }

  const pricing = calculateTotal();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Checkout
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            {activeStep === 0 && <ShippingStep 
              addresses={addresses}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              shippingOption={shippingOption}
              setShippingOption={setShippingOption}
              shippingOptions={shippingOptions}
            />}
            
            {activeStep === 1 && <PaymentStep
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentMethods={paymentMethods}
              billingAddressSame={billingAddressSame}
              setBillingAddressSame={setBillingAddressSame}
              selectedAddress={selectedAddress}
              billingAddress={billingAddress}
              setBillingAddress={setBillingAddress}
            />}
            
            {activeStep === 2 && <ReviewStep
              cartData={cartData}
              selectedAddress={selectedAddress}
              billingAddress={billingAddressSame ? selectedAddress : billingAddress}
              billingAddressSame={billingAddressSame}
              paymentMethod={paymentMethod}
              shippingOption={shippingOption}
              shippingOptions={shippingOptions}
              orderNotes={orderNotes}
              setOrderNotes={setOrderNotes}
              acceptedTerms={acceptedTerms}
              setAcceptedTerms={setAcceptedTerms}
              paymentMethods={paymentMethods}
            />}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                variant="outlined"
              >
                Back
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handlePlaceOrder}
                  disabled={loading || !acceptedTerms}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {loading ? 'Processing...' : paymentMethod === 'payu' ? 'Proceed to Payment' : 'Place Order'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Order Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <OrderSummary 
            cartData={cartData}
            pricing={pricing}
            shippingOption={shippingOption}
            shippingOptions={shippingOptions}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

// Shipping Step Component
const ShippingStep = ({ 
  addresses, 
  selectedAddress, 
  setSelectedAddress,
  shippingOption,
  setShippingOption,
  shippingOptions
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
      Delivery Address
    </Typography>
    
    {addresses.length > 0 ? (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {addresses.map((address, index) => (
          <Grid item xs={12} key={index}>
            <Card 
              variant="outlined" 
              sx={{ 
                cursor: 'pointer',
                border: selectedAddress === address ? 2 : 1,
                borderColor: selectedAddress === address ? 'primary.main' : 'divider'
              }}
              onClick={() => setSelectedAddress(address)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {address.displayType || address.type} Address
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {address.addressLine1}
                      {address.addressLine2 && `, ${address.addressLine2}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {address.city}, {address.state} - {address.pincode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Phone: {address.phoneNumber}
                    </Typography>
                  </Box>
                  <Radio 
                    checked={selectedAddress === address}
                    onChange={() => setSelectedAddress(address)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    ) : (
      <Alert severity="warning" sx={{ mb: 3 }}>
        No saved addresses found. Please add an address in your profile first.
        <Button 
          size="small" 
          onClick={() => window.open('/profile', '_blank')}
          sx={{ ml: 2 }}
        >
          Add Address
        </Button>
      </Alert>
    )}

    <Divider sx={{ my: 3 }} />

    <Typography variant="h6" gutterBottom>
      Delivery Options
    </Typography>
    
    <FormControl component="fieldset">
      <RadioGroup value={shippingOption} onChange={(e) => setShippingOption(e.target.value)}>
  {shippingOptions.map((option) => (
          <FormControlLabel
            key={option.id}
            value={option.id}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">
      {option.name} - ₹{option.price}
                </Typography>
              </Box>
            }
          />
        ))}
      </RadioGroup>
    </FormControl>
  </Box>
);

// Payment Step Component
const PaymentStep = ({ 
  paymentMethod, 
  setPaymentMethod, 
  paymentMethods,
  billingAddressSame,
  setBillingAddressSame,
  selectedAddress,
  billingAddress,
  setBillingAddress
}) => {
  
  const handleBillingAddressChange = (field, value) => {
    setBillingAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
        Payment Method
      </Typography>
      
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          {paymentMethods.map((method) => (
            <FormControlLabel
              key={method.id}
              value={method.id}
              control={<Radio />}
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <method.icon sx={{ mr: 1 }} />
                    <Typography variant="body1">{method.name}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {method.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Billing Address
      </Typography>
      
      <FormControlLabel
        control={
          <Checkbox 
            checked={billingAddressSame} 
            onChange={(e) => setBillingAddressSame(e.target.checked)}
          />
        }
        label="Same as delivery address"
      />

      {billingAddressSame && selectedAddress && (
        <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography variant="body2">
            <strong>{selectedAddress.displayType || selectedAddress.type} Address</strong><br />
            {selectedAddress.addressLine1}
            {selectedAddress.addressLine2 && `, ${selectedAddress.addressLine2}`}
            <br />
            {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
            <br />
            Phone: {selectedAddress.phoneNumber}
          </Typography>
        </Card>
      )}

      {!billingAddressSame && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Enter Billing Address
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={billingAddress.addressLine1}
                onChange={(e) => handleBillingAddressChange('addressLine1', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2 (Optional)"
                value={billingAddress.addressLine2}
                onChange={(e) => handleBillingAddressChange('addressLine2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={billingAddress.city}
                onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={billingAddress.state}
                onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={billingAddress.pincode}
                onChange={(e) => handleBillingAddressChange('pincode', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={billingAddress.phoneNumber}
                onChange={(e) => handleBillingAddressChange('phoneNumber', e.target.value)}
                required
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

// Review Step Component
const ReviewStep = ({ 
  cartData, 
  selectedAddress,
  billingAddress,
  billingAddressSame,
  paymentMethod,
  shippingOption,
  shippingOptions,
  orderNotes,
  setOrderNotes,
  acceptedTerms,
  setAcceptedTerms,
  paymentMethods
}) => {
  const selectedShipping = shippingOptions.find(opt => opt.id === shippingOption);
  const selectedPaymentMethod = paymentMethods.find(pm => pm.id === paymentMethod);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Order Review
      </Typography>
      
      {/* Items Review */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Order Items ({cartData?.items?.length || 0})
          </Typography>
          {cartData?.items?.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <img 
                src={item.image || '/placeholder-image.jpg'} 
                alt={item.name}
                style={{ width: 60, height: 60, objectFit: 'cover', marginRight: 16 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1">{item.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Quantity: {item.quantity} × {item.salePrice && item.salePrice < item.originalPrice ? (
                    <>
                      <strong>₹{item.price}</strong>
                      <span style={{ textDecoration: 'line-through', marginLeft: 4, color: '#888' }}>₹{item.originalPrice}</span>
                    </>
                  ) : `₹${item.price}`}
                </Typography>
                {item.customizations && Object.keys(item.customizations).length > 0 && (
                  <Chip label="Customized" size="small" color="primary" />
                )}
              </Box>
              <Typography variant="body1" fontWeight="bold">
                ₹{item.quantity * item.price}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Delivery Details */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Delivery Details
          </Typography>
          <Typography variant="body2">
            <strong>Delivery Address:</strong><br />
            {selectedAddress?.displayType || selectedAddress?.type} Address<br />
            {selectedAddress?.addressLine1}
            {selectedAddress?.addressLine2 && `, ${selectedAddress.addressLine2}`}
            <br />
            {selectedAddress?.city}, {selectedAddress?.state} - {selectedAddress?.pincode}
            <br />
            <strong>Phone:</strong> {selectedAddress?.phoneNumber}
            <br />
            <strong>Delivery Option:</strong> {selectedShipping?.name} - ₹{selectedShipping?.price}
          </Typography>
        </CardContent>
      </Card>

      {/* Billing Address Details */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Billing Address
          </Typography>
          {billingAddressSame ? (
            <Typography variant="body2">
              <em>Same as delivery address</em><br />
              {selectedAddress?.displayType || selectedAddress?.type} Address<br />
              {selectedAddress?.addressLine1}
              {selectedAddress?.addressLine2 && `, ${selectedAddress.addressLine2}`}
              <br />
              {selectedAddress?.city}, {selectedAddress?.state} - {selectedAddress?.pincode}
              <br />
              <strong>Phone:</strong> {selectedAddress?.phoneNumber}
            </Typography>
          ) : (
            <Typography variant="body2">
              {billingAddress?.addressLine1}
              {billingAddress?.addressLine2 && `, ${billingAddress.addressLine2}`}
              <br />
              {billingAddress?.city}, {billingAddress?.state} - {billingAddress?.pincode}
              <br />
              <strong>Phone:</strong> {billingAddress?.phoneNumber}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Payment Details */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Payment Method
          </Typography>
          <Typography variant="body2">
            {selectedPaymentMethod?.name}
            <br />
            <Typography variant="caption" color="text.secondary">
              {selectedPaymentMethod?.description}
            </Typography>
          </Typography>
        </CardContent>
      </Card>

      {/* Order Notes */}
      <TextField
        fullWidth
        label="Order Notes (Optional)"
        multiline
        rows={3}
        value={orderNotes}
        onChange={(e) => setOrderNotes(e.target.value)}
        sx={{ mb: 3 }}
        placeholder="Any special instructions for your order..."
      />

      {/* Terms and Conditions */}
      <FormControlLabel
        control={
          <Checkbox 
            checked={acceptedTerms} 
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I agree to the{' '}
            <Button variant="text" size="small" sx={{ p: 0, textDecoration: 'underline' }}>
              Terms and Conditions
            </Button>
            {' '}and{' '}
            <Button variant="text" size="small" sx={{ p: 0, textDecoration: 'underline' }}>
              Privacy Policy
            </Button>
          </Typography>
        }
      />
    </Box>
  );
};

// Order Summary Component
const OrderSummary = ({ cartData, pricing, shippingOption, shippingOptions }) => {
  const selectedShipping = shippingOptions.find(opt => opt.id === shippingOption);
  // Show COD charge if present
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>
        <Box sx={{ mb: 2 }}>
          {cartData?.items?.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                {item.name} × {item.quantity}
              </Typography>
              <Typography variant="body2">
                ₹{item.quantity * item.price}
              </Typography>
            </Box>
          ))}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">₹{pricing.subtotal}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Shipping ({selectedShipping?.name})</Typography>
          <Typography variant="body2">₹{pricing.shipping}</Typography>
        </Box>
        {pricing.codCharge > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="warning.main">Cash Handling Charges</Typography>
            <Typography variant="body2" color="warning.main">₹{pricing.codCharge}</Typography>
          </Box>
        )}
        {pricing.discount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="success.main">Discount</Typography>
            <Typography variant="body2" color="success.main">-₹{pricing.discount}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Tax (Included)</Typography>
          <Typography variant="body2">₹{pricing.tax}</Typography>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">Total</Typography>
          <Typography variant="h6" fontWeight="bold">₹{pricing.total}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Checkout;
